import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pendencias } from '../Pendencias';
import { api, ErroApi } from '../api';
import {
  AncoraDePendencias, CabecalhoDaFicha, EscolhaDeBalanca, EtapasDaFicha,
  FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { EquipamentoDaLista, Etapa, Indicador } from '../Ficha';
import { FotosDoRegistro } from '../FotosDoRegistro';
import {
  GraficoDaSaturacao, GraficoDoAdensamento, GraficosDaEnvoltoria,
  GraficosDoCisalhamento,
} from './TriaxialGraficos';
import {
  verTriaxial, verTriaxialCp, gravarTriaxialFicha, criarTriaxialCp,
  editarTriaxialCp, gravarTriaxialUmidade, gravarCriteriosNoLaudo, descartarTriaxialCapsula,
  importarBrutoTriaxial,
} from '../api/triaxial';
import type {
  EnsaioTriaxial, CorpoDeProva, ConferenciaTriaxial,
} from '../api/triaxial';
import { numero, paraNumero, paraCampo, plural, rotulo } from '../formato';
import {
  Area, Aviso, Campo, CampoNumerico, Carregando, ErroDaApi,
  Escolha, Selo, TabelaDeLancamento, Vazio, ZonaDeArquivo, RespostaDaAcao,
} from '../ui';
import type { Opcao } from '../ui';
import {
  LembreteDaEstufa, SecaDaLinha, WDaLinha, naEstufaDaFicha, useEstufaDoEnsaio,
} from './NaEstufa';
import type { Estufa } from './NaEstufa';

/**
 * Lançar o triaxial — CIU, CID e UU.
 *
 * ─── Por que a tela é dividida em fases ──────────────────────────────
 *
 * Porque o ensaio é, e porque foi assim que a Testesolo pediu em
 * 08/09/2026, apontando as abas do software da Suporte: «eles dividem
 * bem as fases dos ensaios, principalmente para os triaxiais».
 *
 * O corpo de prova é moldado, saturado por contrapressão, adensado e só
 * então rompido. Cada etapa responde por si — o B responde pela
 * saturação, o ΔV pelo adensamento, a curva σd × ε pela ruptura —, e uma
 * ficha única esconderia em qual delas o ensaio foi mal. Que é a
 * pergunta que quem olha um triaxial faz.
 *
 * ─── Uma diferença deliberada em relação à Suporte ───────────────────
 *
 * Lá a tara da cápsula é digitada em cada ensaio. Aqui a cápsula é
 * EQUIPAMENTO e a tara vem da verificação vigente no dia da moldagem
 * (`0170`). Custa um cadastro a mais e paga em rastreabilidade: uma
 * cápsula reaferida corrige o histórico inteiro, em vez de deixar cada
 * laudo com o número que estava na tela naquele dia.
 *
 * ─── O que a tela NÃO calcula ────────────────────────────────────────
 *
 * Nada. V₀, γ, γd, e₀, Sr₀, o parâmetro B, as tensões efetivas, o ponto
 * de ruptura e a envoltória vêm prontos do banco (`D-29`). Uma segunda
 * conta aqui seria uma segunda resposta para a mesma pergunta — e a da
 * tela é a que a pessoa vê.
 *
 * ─── E o critério de ruptura ─────────────────────────────────────────
 *
 * É o único campo desta ficha que a tela explica por extenso, porque é
 * o único que MUDA o resultado sem mudar nenhum dado. No CP01 do laudo
 * 2608319 a mesma curva rompe a 2,00 % por máximo de σd e a 4,00 % por
 * máxima razão efetiva. O sistema não escolhe: quem lança declara, corpo
 * de prova a corpo de prova, e o laudo imprime qual valeu.
 */

/*
 * As fases do ensaio viram as ETAPAS da ficha no Carbon (onda 238) —
 * mesma ordem, a do ensaio, e mais a última de toda ficha: fotos e
 * laudo. «O triaxial ganha o registro fotográfico» (quadro, 02/10/2026).
 */
const FASES = [
  { chave: 'amostra', texto: 'Amostra' },
  { chave: 'saturacao', texto: 'Saturação' },
  { chave: 'adensamento', texto: 'Adensamento' },
  { chave: 'cisalhamento', texto: 'Cisalhamento' },
  { chave: 'envoltoria', texto: 'Envoltória' },
  { chave: 'laudo', texto: 'Fotos e laudo' },
] as const;

const TIPOS: Opcao[] = [
  { valor: 'CIU', texto: 'CIU', detalhe: 'consolidado isotrópico, não drenado' },
  { valor: 'CID', texto: 'CID', detalhe: 'consolidado isotrópico, drenado' },
  { valor: 'UU', texto: 'UU', detalhe: 'não consolidado, não drenado' },
];

/*
 * Os critérios de ruptura (0235): os consagrados da mecânica dos solos,
 * calculados sozinhos, e o ponto apontado à mão. Os nomes e as
 * referências são os de `fn_triaxial_criterios` no banco.
 */
const CRITERIOS: Opcao[] = [
  { valor: 'max_desviador', texto: 'Pico de σd', detalhe: 'tensão desviadora — ASTM D4767' },
  { valor: 'max_razao_efetiva', texto: "Pico de σ₁'/σ₃'", detalhe: 'razão de tensões principais — Bishop e Henkel (1962)' },
  { valor: 'pico_poropressao', texto: 'Pico de poropressão', detalhe: 'u máximo — só não drenado' },
  { valor: 'a_zero', texto: 'A = 0', detalhe: 'excesso de poropressão nulo — Skempton (1954)' },
  { valor: 'linha_kf', texto: 'Linha Kf', detalhe: 'trajetória atinge a Kf — Lambe (1964)' },
  { valor: 'deformacao_limite', texto: 'Deformação limitada', detalhe: 'σd na deformação declarada' },
  { valor: 'inflexao', texto: 'Strain-hardening', detalhe: 'De Campos e Carrillo (1995) — ajuste bilinear' },
  { valor: 'manual', texto: 'Manual (apontar)', detalhe: 'quem analisa aponta a leitura' },
];

const CRITERIO_TEXTO: Record<string, string> = {
  max_desviador: 'pico de tensão desviadora — máxima tensão desviadora (ASTM D4767)',
  max_razao_efetiva: "pico de razão de tensões principais σ₁'/σ₃' (Bishop e Henkel, 1962)",
  pico_poropressao: 'pico de poropressão',
  a_zero: 'excesso de poropressão nulo — A = 0 (Skempton, 1954)',
  linha_kf: 'atingir a linha Kf (Lambe, 1964)',
  deformacao_limite: 'deformação limitada',
  inflexao: 'strain-hardening — De Campos e Carrillo (1995), por ajuste bilinear',
  manual: 'ponto apontado por quem analisa',
  outro: 'critério declarado pela Testesolo (ponto apontado)',
};

/** Os critérios que EXIGEM que quem analisa aponte a deformação. */
const APONTADOS = new Set(['manual', 'outro']);

/* Os dois diagramas p–q (0235): as contas são feitas sempre; isto
   escolhe o que o laudo desenha. */
const ESPACOS: Opcao[] = [
  { valor: 'mit', texto: 'MIT', detalhe: 'p = (σ₁+σ₃)/2 · q = (σ₁−σ₃)/2' },
  { valor: 'cambridge', texto: 'Cambridge', detalhe: 'p = (σ₁+2σ₃)/3 · q = σ₁−σ₃' },
  { valor: 'ambos', texto: 'Os dois', detalhe: 'MIT e Cambridge no laudo' },
];

const SATURACOES: Opcao[] = [
  { valor: 'contrapressao_molhado', texto: 'Contrapressão (molhado)' },
  { valor: 'contrapressao_seco', texto: 'Contrapressão (seco)' },
  { valor: 'percolacao', texto: 'Percolação' },
  { valor: 'sem_saturacao', texto: 'Sem saturação' },
];

const SITUACOES: Opcao[] = [
  { valor: 'moldado', texto: 'Moldado' },
  { valor: 'saturando', texto: 'Saturando' },
  { valor: 'adensando', texto: 'Adensando' },
  { valor: 'rompido', texto: 'Rompido' },
  { valor: 'descartado', texto: 'Descartado' },
];

const n = (v: unknown, casas: number, cortar = false) =>
  numero(v, casas, cortar) || '—';

interface Equipamento {
  id: string;
  codigo_patrimonial: string;
  nome: string;
  categoria: string;
}

export function Triaxial() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioTriaxial | null>(null);
  const [cp, setCp] = useState<CorpoDeProva | null>(null);
  const [cpId, setCpId] = useState<string | null>(null);
  const equipamentos = useEquipamentos();
  const capsulas: Equipamento[] = equipamentos;
  const [pendencias, setPendencias] = useState<any[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  /* Sobe a cada gravação: a Estufa do ensaio recarrega junto (onda 294). */
  const [versao, setVersao] = useState(0);

  const carregar = useCallback(async () => {
    try {
      const e = await verTriaxial(id);
      setD(e);
      setCpId((atual) => {
        if (atual && e.corpos.some((c: any) => c.id === atual)) return atual;
        return e.corpos[0]?.id ?? null;
      });
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  useEffect(() => {
    if (!cpId) { setCp(null); return; }
    void (async () => {
      try { setCp(await verTriaxialCp(cpId)); } catch (e) { setErro(e); }
    })();
  }, [cpId]);

  useEffect(() => {
    void (async () => {
      try {
        setPendencias(await api<any[]>(`/ensaios/${id}/pendencias`));
      } catch { setPendencias([]); }
    })();
  }, [id, d]);

  /**
   * Grava e recarrega as DUAS visões.
   *
   * O corpo de prova e o ensaio andam juntos: mexer no critério de um CP
   * muda o ponto de ruptura dele, que muda a envoltória do ensaio. Se a
   * tela recarregasse só o que foi editado, a aba «Envoltória» ficaria
   * mostrando o φ' de antes — e ninguém veria erro nenhum.
   */
  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<unknown>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      await chamada();
      await carregar();
      if (cpId) setCp(await verTriaxialCp(cpId));
      setVersao((v) => v + 1);
      setOk('Gravado.');
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, [carregar, cpId]);

  const campoErro = (c: string) =>
    (erro instanceof ErroApi ? erro.campo(c) : undefined);

  const ficha = useFicha(id, undefined, ok);
  /* As cápsulas na estufa e o carimbo das pesagens (onda 294, RN-241). */
  const estufa = useEstufaDoEnsaio(id, versao);
  const vivosCp = (d?.corpos ?? []).filter((c: any) => c.situacao !== 'descartado');
  const rompidos = (d?.rupturas ?? []).filter((r: any) => !r.motivo_sem_ruptura).length;
  const temEnvoltoria = !!d?.envoltoria && !d.envoltoria.motivo_sem_envoltoria;
  const [etapa, irPara] = useEtapa(FASES.length,
    !d?.cabecalho || vivosCp.length === 0 ? 1
      : rompidos < vivosCp.length ? 4 : temEnvoltoria ? 6 : 5);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o ensaio" />;

  const cab = d.cabecalho ?? null;
  const tipo: string = cab?.tipo ?? 'CIU';
  const fase = FASES[etapa - 1].chave;
  const congelado = fichaCongelada(ficha?.status);
  const e = d.envoltoria;
  const efetiva = e?.base !== 'total';
  const semFase = (f: string) => (tipo === 'UU' && (f === 'saturacao' || f === 'adensamento'));
  const travam = pendencias.filter((p: any) => p.trava && !p.justificada);

  const etapas: Etapa[] = FASES.map((f) => ({
    nome: f.texto,
    feita: f.chave === 'amostra' ? vivosCp.length > 0 && !!d.balanca
      : f.chave === 'cisalhamento' ? vivosCp.length > 0 && rompidos === vivosCp.length
        : f.chave === 'envoltoria' ? temEnvoltoria
          : f.chave === 'laudo' ? ficha?.status === 'aprovado'
            : vivosCp.length > 0 && !semFase(f.chave),
    falta: f.chave === 'amostra' && travam.some((p: any) => String(p.codigo).startsWith('balanca_')),
    resumo: semFase(f.chave) ? 'não se aplica ao UU'
      : f.chave === 'amostra' ? plural(vivosCp.length, 'corpo de prova', 'corpos de prova')
        : f.chave === 'cisalhamento' ? `${rompidos} de ${vivosCp.length} com ruptura`
          : f.chave === 'envoltoria' ? (temEnvoltoria ? `${e.pontos} pontos` : 'precisa de dois')
            : undefined,
  }));

  const indicadores: Indicador[] = [
    { rotulo: efetiva ? "φ'" : 'φ', chave: true, unidade: '°',
      valor: temEnvoltoria ? n(e.phi_graus, 2) : null,
      porque: e?.motivo_sem_envoltoria ?? 'A envoltória precisa de dois pontos de ruptura.',
      como: <>Da reta q = a + p{efetiva ? "'" : ''}·tg α ajustada aos pontos de
        ruptura: sen φ = tg α.</> },
    { rotulo: efetiva ? "c'" : 'c', chave: true, unidade: 'kPa',
      valor: temEnvoltoria ? n(e.coesao_kpa, 2) : null,
      porque: e?.motivo_sem_envoltoria ?? 'A envoltória precisa de dois pontos de ruptura.',
      como: 'c = a / cos φ.' },
    { rotulo: 'R²', valor: temEnvoltoria ? n(e.r2, 4) : null },
    { rotulo: 'Corpos de prova', valor: String(vivosCp.length),
      nota: d.pacote?.cps_por_ensaio ? `de ${d.pacote.cps_por_ensaio} do pacote` : undefined },
    { rotulo: 'Com ruptura', valor: String(rompidos) },
  ];

  return (
    <div className="tela triaxial ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'TRI'}
        titulo={`Triaxial ${cab?.tipo ?? ''}`.trim()} ficha={ficha}
        normaDaTela="—" />
      {cab && (
        <FaixaDaFicha indicadores={indicadores}
          itens={pendencias.filter((p) => !p.justificada).map((p) => p.texto)}
          pendencias={pendencias} />
      )}
      {cab && <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />}

      <AncoraDePendencias>
        <Pendencias itens={pendencias.filter((p) => !p.justificada)
          .map((p) => p.texto)} id={id} detalhe={pendencias}
          aoJustificar={carregar} />
      </AncoraDePendencias>

      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      {!cab ? (
        <FichaNova id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} />
      ) : (
        <>
          {/*
            * As etapas SÃO as fases do ensaio, e a ordem é a do ensaio —
            * não a de importância. Quem está na bancada segue a ordem em
            * que as coisas acontecem.
            */}
          <fieldset className="so-leitura" disabled={congelado}>
          {fase === 'laudo' ? (
            <>
              <FotosDoRegistro
                id={id}
                titulo="Registro fotográfico"
                dica="O corpo de prova moldado, montado na câmara e rompido — o que ajudar a conferir este ensaio depois."
                roteiro={[
                  { tipo: 'cp_antes', nome: 'CP moldado', quando: 'antes do ensaio' },
                  { tipo: 'cp_depois', nome: 'CP rompido', quando: 'na desmontagem' },
                ]}
              />
              <LaudoDaFicha id={id} />
            </>
          ) : fase === 'envoltoria' ? (
            <Envoltoria d={d} gravar={gravar} salvando={salvando}
              aoApontar={(c) => { setCpId(c); irPara(4); }} />
          ) : (
            <>
              <SeletorDeCp d={d} cpId={cpId} aoTrocar={setCpId}
                id={id} gravar={gravar} salvando={salvando} />
              {!cpId ? (
                <Vazio>
                  Nenhum corpo de prova ainda. Crie o primeiro acima —
                  todas as fases são dele.
                </Vazio>
              ) : !cp ? (
                <Carregando texto="Abrindo o corpo de prova" />
              ) : (
                <>
                  {fase === 'amostra' && (
                    <FaseAmostra
                      cab={cab} cp={cp} capsulas={capsulas}
                      equipamentos={equipamentos}
                      balancas={{ balanca: d.balanca?.id ?? '',
                        balancaUmidade: d.balancaUmidade?.id ?? '' }}
                      gravar={gravar} salvando={salvando}
                      campoErro={campoErro} id={id}
                      estufa={estufa} congelado={congelado}
                      recarregar={async () => {
                        await carregar();
                        if (cpId) setCp(await verTriaxialCp(cpId));
                      }} />
                  )}
                  {fase === 'saturacao' && (
                    <FaseSaturacao cp={cp} tipo={tipo} />
                  )}
                  {fase === 'adensamento' && (
                    <FaseAdensamento cp={cp} tipo={tipo} />
                  )}
                  {fase === 'cisalhamento' && (
                    <FaseCisalhamento cp={cp} gravar={gravar} tipo={tipo}
                      salvando={salvando} cabCriterio={cab.criterio_ruptura} />
                  )}
                </>
              )}
            </>
          )}

          </fieldset>
          <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
            nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
        </>
      )}
    </div>
  );
}

// ══ A ficha, quando ainda não existe ══════════════════════════════════

function FichaNova({ id, gravar, salvando, campoErro }: {
  id: string;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  campoErro(c: string): string | undefined;
}) {
  const [tipo, setTipo] = useState('CIU');
  return (
    <section className="bloco">
      <h2>Abrir a ficha do triaxial</h2>
      <p className="explicacao">
        O tipo manda na FORMA das etapas: o UU não adensa, o CID rompe
        drenado, o CIU rompe não drenado. Ele pode ser corrigido depois,
        mas é o que decide o que a tela cobra de você.
      </p>
      <Escolha rotulo="Tipo de ensaio" opcoes={TIPOS} valor={tipo}
        aoEscolher={(v) => setTipo(v ?? 'CIU')} limpavel={false}
        erro={campoErro('tipo')} />
      <button type="button" className="primario"
        disabled={salvando === 'ficha'}
        onClick={() => gravar('ficha',
          () => gravarTriaxialFicha(id, { tipo }))}>
        {salvando === 'ficha' ? 'Abrindo…' : 'Abrir a ficha'}
      </button>
    </section>
  );
}

// ══ O seletor de corpo de prova ═══════════════════════════════════════

function SeletorDeCp({ d, cpId, aoTrocar, id, gravar, salvando }: {
  d: EnsaioTriaxial;
  cpId: string | null;
  aoTrocar(v: string): void;
  id: string;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
}) {
  const proxima = (d.corpos.reduce(
    (m: number, c: any) => Math.max(m, Number(c.ordem) || 0), 0)) + 1;
  /*
   * O pacote (0210, RN-213): «a geração de OS deve ser para o pacote de
   * CPs (3, 4, 5…) para abrir uma ficha de ensaio para o conjunto».
   * Descartado não conta como CP do conjunto.
   */
  const previstos = d.pacote?.cps_por_ensaio ?? null;
  const vivos = d.corpos.filter((c: any) => c.situacao !== 'descartado').length;
  const faltam = previstos ? Math.max(previstos - vivos, 0) : 0;
  const criarOsQueFaltam = async () => {
    for (let k = 0; k < faltam; k++) {
      const o = proxima + k;
      await criarTriaxialCp(id, {
        ordem: o, identificacao: `CP ${o}`, camara: `CT${o <= 99 ? o : 1}`,
      });
    }
  };
  return (
    <div className="seletor-cp">
      {previstos && (
        <p className="pacote-cp" data-pacote={d.pacote?.item_codigo}>
          <b>Pacote {d.pacote?.item_codigo}</b> · {previstos} corpos de prova
          {' '}— {vivos} {vivos === 1 ? 'criado' : 'criados'}
          {faltam > 0 && (
            <button type="button" className="secundario"
              disabled={salvando === 'pacote-cp'}
              onClick={() => gravar('pacote-cp', criarOsQueFaltam)}>
              {salvando === 'pacote-cp' ? 'Criando…'
                : `Criar ${faltam === 1 ? 'o que falta' : `os ${faltam} que faltam`}`}
            </button>
          )}
        </p>
      )}
      <div className="chips-cp" role="tablist" aria-label="Corpos de prova">
        {d.corpos.map((c: any) => (
          <button key={c.id} type="button" role="tab"
            aria-selected={c.id === cpId}
            className={`chip-cp${c.id === cpId ? ' corrente' : ''}${
              c.situacao === 'descartado' ? ' descartado' : ''}`}
            onClick={() => aoTrocar(c.id)}>
            <b>{c.identificacao}</b>
            <span className="sub">
              σ₃&apos; alvo {n(c.tensao_confinante_alvo_kpa, 0)} kPa
            </span>
          </button>
        ))}
        <button type="button" className="chip-cp novo"
          disabled={salvando === 'novo-cp'}
          onClick={() => gravar('novo-cp', () => criarTriaxialCp(id, {
            ordem: proxima,
            identificacao: `CP ${proxima}`,
            camara: `CT${proxima <= 99 ? proxima : 1}`,
          }))}>
          + Corpo de prova
        </button>
      </div>
      <p className="explicacao">
        Os corpos de prova são independentes: cada um tem a sua geometria,
        a sua saturação, o seu adensamento e o seu critério de ruptura. O
        que eles compartilham é a envoltória — que só existe com dois ou
        mais.
      </p>
    </div>
  );
}

// ══ Fase 1 · a amostra e o corpo de prova ═════════════════════════════

function FaseAmostra({
  cab, cp, capsulas, equipamentos, balancas, gravar, salvando, campoErro, id,
  recarregar, estufa, congelado,
}: {
  cab: any;
  cp: CorpoDeProva;
  capsulas: Equipamento[];
  equipamentos: EquipamentoDaLista[];
  balancas: { balanca: string; balancaUmidade: string };
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  campoErro(c: string): string | undefined;
  id: string;
  recarregar(): Promise<void>;
  estufa: Estufa;
  congelado: boolean;
}) {
  const daEstufa = { estufa, congelado, recarregar };
  return (
    <>
      <Ficha cab={cab} id={id} gravar={gravar} salvando={salvando}
        campoErro={campoErro} equipamentos={equipamentos} balancas={balancas} />
      <DadosDoCp cp={cp} gravar={gravar} salvando={salvando}
        campoErro={campoErro} />
      <Capsulas cp={cp} etapa="moldagem" capsulas={capsulas}
        gravar={gravar} salvando={salvando} {...daEstufa} />
      <Indices cp={cp.cp} />
      <Capsulas cp={cp} etapa="final" capsulas={capsulas}
        gravar={gravar} salvando={salvando} {...daEstufa} />
      <Importar cpId={cp.cp.id} recarregar={recarregar} />
    </>
  );
}

function Ficha({ cab, id, gravar, salvando, campoErro, equipamentos, balancas }: {
  cab: any;
  id: string;
  equipamentos: EquipamentoDaLista[];
  balancas: { balanca: string; balancaUmidade: string };
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  campoErro(c: string): string | undefined;
}) {
  const [f, setF] = useState(() => ({
    tipo: cab.tipo as string,
    diametroNominalMm: paraCampo(cab.diametro_nominal_mm),
    alturaNominalMm: paraCampo(cab.altura_nominal_mm),
    massaEspecificaGraos: paraCampo(cab.massa_especifica_graos),
    graosAssumida: !!cab.graos_assumida,
    espessuraMembranaMm: paraCampo(cab.espessura_membrana_mm),
    moduloMembranaKpa: paraCampo(cab.modulo_membrana_kpa),
    metodoSaturacao: (cab.metodo_saturacao ?? null) as string | null,
    criterioRuptura: (cab.criterio_ruptura ?? null) as string | null,
    criterioRupturaFonte: cab.criterio_ruptura_fonte ?? '',
    espacoTensoes: (cab.espaco_tensoes ?? 'mit') as string,
    deformacaoLimitePct: paraCampo(cab.deformacao_limite_pct),
    observacoes: cab.observacoes ?? '',
    balanca: balancas.balanca,
    balancaUmidade: balancas.balancaUmidade,
  }));
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    setF((x) => ({ ...x, balanca: balancas.balanca,
      balancaUmidade: balancas.balancaUmidade }));
  }, [balancas.balanca, balancas.balancaUmidade]);

  return (
    <section className="bloco">
      <h2>Ficha do ensaio</h2>
      {/*
        * As balanças das duas baterias (RN-215, onda 238): as massas dos
        * corpos de prova e as cápsulas. Uma escolha por bateria — todos os
        * CPs do ensaio vão para a mesma balança.
        */}
      <div className="grade-campos fe-campos">
        <EscolhaDeBalanca rotulo="Balança das massas dos CPs (m₀, m_f)"
          equipamentos={equipamentos} valor={f.balanca}
          aoEscolher={(v) => set('balanca', v)}
          erro={campoErro('balancaEquipamentoId')} />
        <EscolhaDeBalanca rotulo="Balança das cápsulas de umidade"
          equipamentos={equipamentos} valor={f.balancaUmidade}
          aoEscolher={(v) => set('balancaUmidade', v)}
          erro={campoErro('balancaUmidadeEquipamentoId')} />
      </div>
      <div className="grade-campos">
        <Escolha rotulo="Tipo" opcoes={TIPOS} valor={f.tipo}
          aoEscolher={(v) => set('tipo', v ?? f.tipo)} limpavel={false} />
        <CampoNumerico rotulo="Diâmetro nominal (mm)"
          value={f.diametroNominalMm}
          onChange={(e) => set('diametroNominalMm', e.target.value)} />
        <CampoNumerico rotulo="Altura nominal (mm)"
          value={f.alturaNominalMm}
          onChange={(e) => set('alturaNominalMm', e.target.value)} />
        <CampoNumerico rotulo="Massa específica dos grãos ρs (g/cm³)"
          value={f.massaEspecificaGraos}
          erro={campoErro('massaEspecificaGraos')}
          dica="Sem ela não há índice de vazios nem grau de saturação."
          onChange={(e) => set('massaEspecificaGraos', e.target.value)} />
        <label className="campo campo-marca">
          <input type="checkbox" checked={f.graosAssumida}
            onChange={(e) => set('graosAssumida', e.target.checked)} />
          <span>
            ρs <b>assumida</b>, e não medida — o laudo diz isso ao lado do
            número, porque um e₀ calculado sobre um valor assumido não é o
            mesmo dado que um calculado sobre ensaio.
          </span>
        </label>
        <CampoNumerico rotulo="Espessura da membrana (mm)"
          value={f.espessuraMembranaMm}
          onChange={(e) => set('espessuraMembranaMm', e.target.value)} />
        <CampoNumerico rotulo="Módulo da membrana (kPa)"
          value={f.moduloMembranaKpa}
          onChange={(e) => set('moduloMembranaKpa', e.target.value)} />
        <Escolha rotulo="Método de saturação" opcoes={SATURACOES}
          valor={f.metodoSaturacao}
          aoEscolher={(v) => set('metodoSaturacao', v)} />
      </div>

      {/*
        * O critério de ruptura saiu daqui (onda 275): «os critérios de
        * ruptura dos CPs devem ser avaliados na aba envoltória … depende
        * do resultado de todos os cps» — Humberto, 04/10/2026. A ficha
        * continua mandando o que está gravado (o estado nasce do
        * cabeçalho), para gravar a ficha não apagar a escolha feita lá.
        */}
      <p className="explicacao">
        O critério de ruptura, a deformação limite e o diagrama p–q do
        laudo se escolhem na aba <b>Envoltória</b>, com todos os corpos de
        prova lado a lado.
      </p>

      <Area rotulo="Observações do ensaio" rows={2} value={f.observacoes}
        dica="O que não se deduz de dado nenhum: amostra amolgada no topo, membrana trocada, uma paralisação. O sistema não escreve isso por você."
        onChange={(e) => set('observacoes', e.target.value)} />

      <button type="button" className="primario"
        disabled={salvando === 'ficha'}
        onClick={() => gravar('ficha', () => gravarTriaxialFicha(id, {
          tipo: f.tipo,
          diametroNominalMm: paraNumero(f.diametroNominalMm),
          alturaNominalMm: paraNumero(f.alturaNominalMm),
          massaEspecificaGraos: paraNumero(f.massaEspecificaGraos),
          graosAssumida: f.graosAssumida,
          espessuraMembranaMm: paraNumero(f.espessuraMembranaMm),
          moduloMembranaKpa: paraNumero(f.moduloMembranaKpa),
          metodoSaturacao: f.metodoSaturacao ?? undefined,
          criterioRuptura: f.criterioRuptura ?? undefined,
          criterioRupturaFonte: f.criterioRupturaFonte || undefined,
          deformacaoLimitePct: paraNumero(f.deformacaoLimitePct),
          espacoTensoes: f.espacoTensoes || undefined,
          observacoes: f.observacoes || undefined,
          balancaEquipamentoId: f.balanca || undefined,
          balancaUmidadeEquipamentoId: f.balancaUmidade || undefined,
        }))}>
        {salvando === 'ficha' ? 'Gravando…' : 'Gravar a ficha'}
      </button>
    </section>
  );
}

function DadosDoCp({ cp, gravar, salvando, campoErro }: {
  cp: CorpoDeProva;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  campoErro(c: string): string | undefined;
}) {
  const c = cp.cp;
  const [f, setF] = useState(() => ({
    identificacao: c.identificacao ?? '',
    camara: c.camara ?? '',
    diametroMm: paraCampo(c.diametro_mm),
    alturaMm: paraCampo(c.altura_mm),
    tensaoConfinanteAlvoKpa: paraCampo(c.tensao_confinante_alvo_kpa),
    massaInicialG: paraCampo(c.massa_inicial_g),
    massaFinalG: paraCampo(c.massa_final_g),
    dataMoldagem: (c.data_moldagem ?? '').slice(0, 10),
    dataRuptura: (c.data_ruptura ?? '').slice(0, 10),
    situacao: c.situacao as string,
    motivoDescarte: c.motivo_descarte ?? '',
    observacoes: c.observacoes ?? '',
  }));
  /* Trocar de corpo de prova troca o formulário inteiro. Sem isto, os
     campos do CP 1 ficariam na tela do CP 2 — e seriam gravados nele. */
  useEffect(() => {
    setF({
      identificacao: c.identificacao ?? '',
      camara: c.camara ?? '',
      diametroMm: paraCampo(c.diametro_mm),
      alturaMm: paraCampo(c.altura_mm),
      tensaoConfinanteAlvoKpa: paraCampo(c.tensao_confinante_alvo_kpa),
      massaInicialG: paraCampo(c.massa_inicial_g),
      massaFinalG: paraCampo(c.massa_final_g),
      dataMoldagem: (c.data_moldagem ?? '').slice(0, 10),
      dataRuptura: (c.data_ruptura ?? '').slice(0, 10),
      situacao: c.situacao as string,
      motivoDescarte: c.motivo_descarte ?? '',
      observacoes: c.observacoes ?? '',
    });
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));

  return (
    <section className="bloco">
      <h2>Corpo de prova {c.identificacao}</h2>
      <div className="grade-campos">
        <Campo rotulo="Identificação" value={f.identificacao}
          onChange={(e) => set('identificacao', e.target.value)} />
        <Campo rotulo="Câmara" value={f.camara} placeholder="CT1"
          erro={campoErro('camara')}
          dica="É por ela que a planilha das quatro câmaras acha este corpo de prova."
          onChange={(e) => set('camara', e.target.value.toUpperCase())} />
        <CampoNumerico rotulo="Diâmetro D₀ (mm)" value={f.diametroMm}
          onChange={(e) => set('diametroMm', e.target.value)} />
        <CampoNumerico rotulo="Altura H₀ (mm)" value={f.alturaMm}
          onChange={(e) => set('alturaMm', e.target.value)} />
        <CampoNumerico rotulo="σ₃' de adensamento — alvo (kPa)"
          value={f.tensaoConfinanteAlvoKpa}
          onChange={(e) => set('tensaoConfinanteAlvoKpa', e.target.value)} />
        <CampoNumerico rotulo="Massa úmida na moldagem m₀ (g)"
          value={f.massaInicialG} erro={campoErro('massaInicialG')}
          dica="É o numerador de γ = m₀/V. Sem ela não há γ, e sem γ não há e₀."
          onChange={(e) => set('massaInicialG', e.target.value)} />
        <CampoNumerico rotulo="Massa úmida na desmontagem m_f (g)"
          value={f.massaFinalG}
          onChange={(e) => set('massaFinalG', e.target.value)} />
        <Campo rotulo="Data da moldagem" type="date" value={f.dataMoldagem}
          onChange={(e) => set('dataMoldagem', e.target.value)} />
        <Campo rotulo="Data da ruptura" type="date" value={f.dataRuptura}
          onChange={(e) => set('dataRuptura', e.target.value)} />
      </div>

      <div className="grade-campos">
        <Escolha rotulo="Situação" opcoes={SITUACOES} valor={f.situacao}
          aoEscolher={(v) => set('situacao', v ?? f.situacao)}
          limpavel={false} />
        {f.situacao === 'descartado' && (
          <Campo rotulo="Motivo do descarte" value={f.motivoDescarte}
            erro={campoErro('motivoDescarte')}
            onChange={(e) => set('motivoDescarte', e.target.value)} />
        )}
        <Area rotulo="Observações do corpo de prova" rows={2}
          value={f.observacoes}
          onChange={(e) => set('observacoes', e.target.value)} />
      </div>

      <button type="button" className="primario"
        disabled={salvando === 'cp'}
        onClick={() => gravar('cp', () => editarTriaxialCp(c.id, {
          identificacao: f.identificacao,
          camara: f.camara || null,
          diametroMm: paraNumero(f.diametroMm) ?? null,
          alturaMm: paraNumero(f.alturaMm) ?? null,
          tensaoConfinanteAlvoKpa:
            paraNumero(f.tensaoConfinanteAlvoKpa) ?? null,
          massaInicialG: paraNumero(f.massaInicialG) ?? null,
          massaFinalG: paraNumero(f.massaFinalG) ?? null,
          dataMoldagem: f.dataMoldagem || null,
          dataRuptura: f.dataRuptura || null,
          situacao: f.situacao,
          motivoDescarte: f.motivoDescarte || null,
          observacoes: f.observacoes || null,
        }))}>
        {salvando === 'cp' ? 'Gravando…' : 'Gravar o corpo de prova'}
      </button>
    </section>
  );
}

// ── As cápsulas de umidade ──────────────────────────────────────────

/*
 * As cápsulas de UMA etapa do corpo de prova — gravadas em cima, novas
 * embaixo (onda 294).
 *
 * Até a 0246 as linhas gravadas voltavam editáveis e o «Gravar» reenviava
 * todas. Agora a linha gravada trava como nos triviais (RN-236): a massa
 * úmida aparece como texto, a seca pesa na própria coluna enquanto a
 * cápsula está na estufa (RN-241), e a correção é pelo «Corrigir». Só a
 * linha nova sobe.
 */
interface CapsulaNova {
  ordem: number;
  capsulaEquipamentoId: string;
  umido: string;
  seco: string;
}

function Capsulas({ cp, etapa, capsulas, gravar, salvando, estufa, congelado,
  recarregar }: {
  cp: CorpoDeProva;
  etapa: 'moldagem' | 'final';
  capsulas: Equipamento[];
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  estufa: Estufa;
  congelado: boolean;
  recarregar(): Promise<void>;
}) {
  const linhas = useMemo(
    () => cp.umidade.filter((u: any) => u.etapa === etapa),
    [cp.umidade, etapa]);

  const [descartando, setDescartando] = useState<any | null>(null);
  const [novas, setNovas] = useState<CapsulaNova[]>([]);
  useEffect(() => { setNovas([]); }, [cp.cp.id]);

  const titulo = etapa === 'moldagem'
    ? 'Cápsulas de umidade — moldagem'
    : 'Cápsulas de umidade — desmontagem';
  const marca = `caps-${etapa}`;

  const set = (i: number, k: keyof CapsulaNova, v: string) => setNovas(
    (x) => x.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const proxima = () => Math.max(
    linhas.reduce((m: number, u: any) => Math.max(m, u.ordem), 0),
    novas.reduce((m, l) => Math.max(m, l.ordem), 0)) + 1;

  const opcoes: Opcao[] = capsulas.map((c) => ({
    valor: c.id, texto: c.codigo_patrimonial, detalhe: c.nome,
  }));
  /* A seca é opcional: sem ela, a cápsula vai para a estufa (RN-241). */
  const prontas = novas.filter((l) => l.capsulaEquipamentoId
    && paraNumero(l.umido) !== undefined);
  const naEstufa = linhas.filter(naEstufaDaFicha).length;

  const lancadas = linhas.map((u: any) => ({
    chave: `${etapa}-${u.id}`,
    classe: u.descartada ? 'descartada' : undefined,
    titulo: `Cápsula ${u.ordem}`,
    resultado: u.descartada ? '—' : naEstufaDaFicha(u) ? 'na estufa' : `${n(u.umidade, 2)} %`,
    semCartao: [0, 5],
    celulas: [
      u.ordem,
      <span key="c" className="cod">{u.capsula ?? '—'}</span>,
      n(u.tara, 3),
      n(u.massa_capsula_solo_umido, 2),
      <SecaDaLinha key="s" linha={u} estufa={estufa} congelado={congelado}
        opcoesCapsula={opcoes} aoMudou={recarregar} />,
      <WDaLinha key="w" linha={u} />,
      !u.descartada ? (
        /* Descarte com motivo, numa janela — o `window.prompt`
           de antes era um diálogo do navegador, que trava a
           página e não diz o mínimo de caracteres (onda 238). */
        <button key="d" type="button"
          className="botao discreto pequeno fe-descartar"
          aria-label={`Descartar a cápsula ${u.ordem}`}
          onClick={() => setDescartando(u)}>Descartar</button>
      ) : (
        <span key="d" className="nada">
          descartada — {u.motivo_descarte}
        </span>
      ),
    ],
  }));

  const emBranco = novas.map((l, i) => ({
    chave: `${etapa}-nova-${l.ordem}`,
    classe: 'nova',
    titulo: `Cápsula ${l.ordem} · nova`,
    resultado: '—',
    semCartao: [0, 5],
    celulas: [
      l.ordem,
      <select key="c" value={l.capsulaEquipamentoId}
        aria-label="Cápsula"
        onChange={(e) => set(i, 'capsulaEquipamentoId', e.target.value)}>
        <option value="">Escolha…</option>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>{o.texto}</option>
        ))}
      </select>,
      '—',
      <CampoNumerico key="u" rotulo="Cáps. + solo úmido (g)"
        rotuloOculto value={l.umido}
        onChange={(e) => set(i, 'umido', e.target.value)} />,
      <CampoNumerico key="s" rotulo="Cáps. + solo seco (g)"
        rotuloOculto value={l.seco} placeholder="ou na estufa"
        onChange={(e) => set(i, 'seco', e.target.value)} />,
      '—',
      <button key="r" type="button" className="remover" tabIndex={-1}
        aria-label={`Remover a cápsula ${l.ordem}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovas((x) => x.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  return (
    <section className="bloco">
      <h2>{titulo}</h2>
      {etapa === 'moldagem' && (
        <p className="explicacao">
          A tara não se digita: ela vem da verificação vigente da cápsula
          no dia da moldagem. Uma cápsula recalibrada corrige o histórico
          inteiro — e um teor calculado com tara zero passaria por número.
        </p>
      )}

      {linhas.length === 0 && novas.length === 0 ? (
        <Vazio>Nenhuma cápsula lançada nesta etapa.</Vazio>
      ) : (
        <TabelaDeLancamento
          rotulo={titulo}
          colunas={[
            { texto: 'Nº', num: true },
            { texto: 'Cápsula' },
            { texto: 'Tara (g)', num: true },
            { texto: 'Cáps. + solo úmido (g)', num: true },
            { texto: 'Cáps. + solo seco (g)', num: true },
            { texto: 'w (%)', num: true },
            { texto: '' },
          ]}
          linhas={[...lancadas, ...emBranco]} />
      )}
      {!congelado && (linhas.length > 0 || novas.length > 0)
        && <LembreteDaEstufa quantas={naEstufa} />}

      <div className="acoes">
        <button type="button"
          onClick={() => setNovas((x) => [...x, {
            ordem: proxima(), capsulaEquipamentoId: '', umido: '', seco: '',
          }])}>
          + Cápsula
        </button>
        <button type="button" className="primario" data-gravar-tabela=""
          disabled={salvando === marca || !prontas.length}
          onClick={async () => {
            const feito = await gravar(marca, () => gravarTriaxialUmidade(cp.cp.id, {
              etapa,
              capsulas: prontas.map((l) => ({
                ordem: l.ordem,
                capsulaEquipamentoId: l.capsulaEquipamentoId,
                massaCapsulaSoloUmido: paraNumero(l.umido),
                ...(paraNumero(l.seco) !== undefined
                  ? { massaCapsulaSoloSeco: paraNumero(l.seco) } : {}),
              })),
            }));
            if (feito) setNovas((x) => x.filter((l) => !prontas.includes(l)));
          }}>
          {salvando === marca ? 'Gravando…' : 'Gravar as cápsulas'}
        </button>
      </div>
      <JanelaDeDescarte
        alvo={descartando ? `a cápsula ${descartando.ordem}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar(marca, () => descartarTriaxialCapsula(
          cp.cp.id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </section>
  );
}

function Indices({ cp }: { cp: any }) {
  const item = (rot: string, val: string, dica?: string) => (
    <div className="indice" key={rot}>
      <dt>{rot}</dt>
      <dd>{val}</dd>
      {dica && <p className="sub">{dica}</p>}
    </div>
  );
  return (
    <section className="bloco">
      <h2>Índices físicos</h2>
      <dl className="grade-indices">
        {item('V₀ (cm³)', n(cp.volume_inicial_cm3, 3))}
        {item('w₀ (%)', n(cp.umidade_inicial_pct, 2))}
        {item('γ (g/cm³)', n(cp.massa_especifica_umida, 4))}
        {item('γd (g/cm³)', n(cp.massa_especifica_seca, 4))}
        {item('e₀', n(cp.indice_vazios_inicial, 4))}
        {item('Sr₀ (%)', n(cp.grau_saturacao_inicial, 1))}
        {item('m_sd (g)', n(cp.massa_seca_g, 3))}
        {item('B adotado', n(cp.parametro_b_adotado, 3))}
        {item('ΔV do adensamento (cm³)',
          n(cp.variacao_volume_adensamento_cm3, 3))}
        {item('V_c (cm³)', n(cp.volume_apos_adensamento_cm3, 3))}
        {item('e_c', n(cp.indice_vazios_apos_adensamento, 4))}
        {item('w_f (%)', n(cp.umidade_final_pct, 2))}
        {item('Δw (%)', n(cp.variacao_umidade_pct, 2))}
        {item('ΔM (g)', n(cp.variacao_massa_g, 3))}
      </dl>
      <p className="explicacao">
        V = π·D₀²/4·H₀ · γ = m₀/V · γd = γ/(1 + w₀/100) · e₀ = ρs/γd − 1 ·
        Sr₀ = w₀·ρs/e₀. Nenhum deles é digitado, e nenhum é calculado
        aqui: eles vêm do banco, que é onde a conta mora.
      </p>
    </section>
  );
}

// ── A importação do bruto ───────────────────────────────────────────

function Importar({ cpId, recarregar }: {
  cpId: string; recarregar(): Promise<void>;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [conf, setConf] = useState<ConferenciaTriaxial | null>(null);
  const [corpoDoArquivo, setCorpo] = useState<string>('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  /* Trocar de corpo de prova apaga a conferência. Ela é DAQUELE CP, e
     deixá-la na tela faria confirmar a importação no corpo errado. */
  useEffect(() => { setConf(null); setArquivo(null); setCorpo(''); }, [cpId]);

  async function rodar(simular: boolean) {
    if (!arquivo) return;
    setOcupado(true); setErro(null);
    try {
      const r = await importarBrutoTriaxial(cpId, arquivo, {
        simular, corpoDoArquivo: corpoDoArquivo || undefined,
      });
      setConf(r);
      if (!simular) await recarregar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  return (
    <section className="bloco">
      <h2>Arquivo da prensa</h2>
      <p className="explicacao">
        O arquivo traz as TRÊS etapas de uma vez — saturação, adensamento
        e ruptura. A tela simula antes de gravar: trazer três etapas e só
        então descobrir que o arquivo era do corpo de prova vizinho é o
        erro que não se desfaz num banco que não apaga.
      </p>
      <ZonaDeArquivo rotulo="Arquivo da prensa" tipos="CSV ou XLSX"
        aceita=".csv,.xlsx,.xls,text/csv" arquivos={arquivo}
        desabilitado={ocupado}
        aoEscolher={(f) => { setArquivo(f[0] ?? null); setConf(null); }} />

      {erro ? <ErroDaApi erro={erro} /> : null}

      {conf && (
        <div className="conferencia">
          <h3>O que o arquivo tem</h3>
          <p>
            <b>{conf.arquivo.identificacao ?? '(sem identificação)'}</b>
            {conf.arquivo.camara ? ` · câmara ${conf.arquivo.camara}` : ''}
            {conf.arquivo.numeroTeste ? ` · NT ${conf.arquivo.numeroTeste}` : ''}
            {' → '}
            <b>{conf.corpoDeProva.identificacao}</b>
            {conf.corpoDeProva.camara
              ? ` · câmara ${conf.corpoDeProva.camara}` : ''}
          </p>
          <ul className="lista-conf">
            <li>{conf.saturacao} degrau(s) de saturação</li>
            <li>{conf.adensamento} leitura(s) de adensamento</li>
            <li>{conf.cisalhamento} ponto(s) de cisalhamento</li>
          </ul>
          {conf.degraus.length > 0 && (
            <div className="rolar-lado"><table className="dados">
              <caption>
                O B calculado das quatro medidas, ao lado do que o
                equipamento gravou
              </caption>
              <thead><tr>
                <th className="num">Degrau</th>
                <th className="num">Contrapressão (kPa)</th>
                <th className="num">B calculado</th>
                <th className="num">B do equipamento</th>
              </tr></thead>
              <tbody>
                {conf.degraus.map((g) => (
                  <tr key={g.ordem}>
                    <td className="num">{g.ordem}</td>
                    <td className="num">{n(g.contrapressaoKpa, 2)}</td>
                    <td className="num">{n(g.parametroB, 3)}</td>
                    <td className="num">{n(g.parametroBDoEquipamento, 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          {conf.corposNoArquivo.length > 1 && (
            <label className="campo">
              <span className="rotulo">Qual corpo do arquivo</span>
              <select value={corpoDoArquivo}
                onChange={(e) => setCorpo(e.target.value)}>
                <option value="">Escolha…</option>
                {conf.corposNoArquivo.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <span className="dica">
                Nunca «o primeiro»: na planilha das quatro câmaras o
                primeiro CT1 pode ser de outro mês.
              </span>
            </label>
          )}
          {conf.avisos.length > 0 && (
            <Aviso tipo="atencao" titulo="O leitor avisou">
              <ul>{conf.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </Aviso>
          )}
          {conf.gravado && (
            <Aviso tipo="ok" titulo="Importado">
              {conf.gravado.saturacao} degrau(s), {conf.gravado.adensamento}{' '}
              leitura(s) de adensamento e {conf.gravado.cisalhamento}{' '}
              ponto(s) de cisalhamento.
            </Aviso>
          )}
        </div>
      )}

      <div className="acoes">
        <button type="button" disabled={!arquivo || ocupado}
          onClick={() => rodar(true)}>
          {ocupado ? 'Lendo…' : 'Conferir sem gravar'}
        </button>
        <button type="button" className="primario"
          disabled={!arquivo || ocupado || !conf}
          onClick={() => rodar(false)}>
          Importar
        </button>
      </div>
    </section>
  );
}

// ══ Fase 2 · saturação ════════════════════════════════════════════════

function FaseSaturacao({ cp, tipo }: { cp: CorpoDeProva; tipo: string }) {
  if (tipo === 'UU') {
    return (
      <section className="bloco">
        <h2>Saturação</h2>
        <Vazio>
          O ensaio é UU: por definição ele não é saturado por
          contrapressão nem adensado. Vá direto ao cisalhamento.
        </Vazio>
      </section>
    );
  }
  return (
    <section className="bloco">
      <h2>Saturação por contrapressão</h2>
      <p className="explicacao">
        B = Δu / Δσ₃, medido em cada degrau com a drenagem fechada. Os
        degraus vêm do arquivo da prensa — a prensa da Testesolo já
        calcula o B, e o que a tela mostra é a conta refeita das quatro
        medidas ao lado dele.
      </p>
      {!cp.saturacao.length ? (
        <Vazio>
          Nenhum degrau lançado. Importe o arquivo da prensa na aba
          «Amostra».
        </Vazio>
      ) : (
        <div className="rolar-lado"><table className="dados">
          <thead><tr>
            <th className="num">Degrau</th>
            <th className="num">Contrapressão (kPa)</th>
            <th className="num">σ₃ ref.</th>
            <th className="num">u ref.</th>
            <th className="num">σ₃ inc.</th>
            <th className="num">u inc.</th>
            <th className="num">Δσ₃</th>
            <th className="num">Δu</th>
            <th className="num">B</th>
          </tr></thead>
          <tbody>
            {cp.saturacao.map((s: any) => (
              <tr key={s.id} className={s.descartado ? 'descartada' : ''}>
                <td className="num">{s.ordem}</td>
                <td className="num">{n(s.contrapressao_kpa, 2)}</td>
                <td className="num">{n(s.sigma3_ref_kpa, 3)}</td>
                <td className="num">{n(s.u_ref_kpa, 3)}</td>
                <td className="num">{n(s.sigma3_inc_kpa, 3)}</td>
                <td className="num">{n(s.u_inc_kpa, 3)}</td>
                <td className="num">{n(s.delta_sigma3_kpa, 3)}</td>
                <td className="num">{n(s.delta_u_kpa, 3)}</td>
                <td className="num forte">{n(s.parametro_b, 3)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <GraficoDaSaturacao cp={cp} />
    </section>
  );
}

// ══ Fase 3 · adensamento ══════════════════════════════════════════════

function FaseAdensamento({ cp, tipo }: { cp: CorpoDeProva; tipo: string }) {
  if (tipo === 'UU') {
    return (
      <section className="bloco">
        <h2>Adensamento</h2>
        <Vazio>O ensaio é UU: ele não adensa.</Vazio>
      </section>
    );
  }
  const ult = cp.adensamento[cp.adensamento.length - 1];
  return (
    <section className="bloco">
      <h2>Adensamento</h2>
      <p className="explicacao">
        O t₅₀ e a velocidade de cisalhamento que sai dele NÃO são
        calculados: a construção que os obtém é escolha técnica, e o
        sistema não a presume.
      </p>
      {!cp.adensamento.length ? (
        <Vazio>
          Nenhuma leitura lançada. Importe o arquivo da prensa na aba
          «Amostra».
        </Vazio>
      ) : (
        <>
          <div className="resumo-fase">
            <Selo tom="neutro">
              {cp.cp.modo_adensamento === 'incremental'
                ? 'Incremental' : 'Contínuo'}
            </Selo>
            <span>{cp.adensamento.length} leitura(s)</span>
            <span>ΔV acumulado {n(ult?.variacao_volume_cm3, 3)} cm³</span>
            <span>V_c {n(cp.cp.volume_apos_adensamento_cm3, 3)} cm³</span>
            <span>e_c {n(cp.cp.indice_vazios_apos_adensamento, 4)}</span>
          </div>
          <GraficoDoAdensamento cp={cp} />
          <div className="rolar-lado"><table className="dados">
            <thead><tr>
              <th className="num">t (min)</th>
              <th className="num">σ₃ (kPa)</th>
              <th className="num">u (kPa)</th>
              <th className="num">ΔV (cm³)</th>
              <th className="num">PC (kPa)</th>
              <th className="num">CP (kPa)</th>
            </tr></thead>
            <tbody>
              {cp.adensamento.map((a: any) => (
                <tr key={a.id} className={a.descartada ? 'descartada' : ''}>
                  <td className="num">{n(a.tempo_min, 4, true)}</td>
                  <td className="num">{n(a.tensao_confinante_kpa, 3)}</td>
                  <td className="num">{n(a.poropressao_kpa, 3)}</td>
                  <td className="num">{n(a.variacao_volume_cm3, 4)}</td>
                  <td className="num">{n(a.pressao_camara_kpa, 3)}</td>
                  <td className="num">{n(a.contrapressao_kpa, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </>
      )}
    </section>
  );
}

// ══ Fase 4 · cisalhamento ═════════════════════════════════════════════

function FaseCisalhamento({ cp, gravar, salvando, cabCriterio, tipo }: {
  cp: CorpoDeProva;
  tipo: string;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  cabCriterio: string | null;
}) {
  const r = cp.ruptura;
  const efetivo = cp.cp.criterio_ruptura ?? cabCriterio;
  const defRuptura = r?.deformacao_axial_pct != null
    ? Number(r.deformacao_axial_pct) : null;

  return (
    <section className="bloco">
      <h2>Cisalhamento</h2>

      <div className="ruptura-cartao">
        <h3>Ponto de ruptura</h3>
        {r?.motivo_sem_ruptura ? (
          <Aviso tipo="atencao" titulo="Sem ponto de ruptura">
            {r.motivo_sem_ruptura}.
            {!efetivo && ' Declare o critério na aba «Envoltória».'}
          </Aviso>
        ) : r ? (
          <>
            <p className="explicacao">
              Sob o critério declarado: <b>{
                CRITERIO_TEXTO[r.criterio] ?? r.criterio}</b>
              {cp.cp.criterio_ruptura
                ? ' — deste corpo de prova.'
                : ' — padrão do ensaio.'}
            </p>
            <dl className="grade-indices">
              <div className="indice"><dt>ε (%)</dt>
                <dd>{n(r.deformacao_axial_pct, 3)}</dd></div>
              <div className="indice"><dt>σd (kPa)</dt>
                <dd>{n(r.tensao_desvio_kpa, 2)}</dd></div>
              <div className="indice"><dt>u (kPa)</dt>
                <dd>{n(r.poropressao_kpa, 2)}</dd></div>
              <div className="indice"><dt>σ₃&apos; (kPa)</dt>
                <dd>{n(r.sigma3_efetiva_kpa, 2)}</dd></div>
              <div className="indice"><dt>σ₁&apos; (kPa)</dt>
                <dd>{n(r.sigma1_efetiva_kpa, 2)}</dd></div>
              <div className="indice"><dt>p&apos; (kPa)</dt>
                <dd>{n(r.p_efetiva_kpa, 2)}</dd></div>
              <div className="indice"><dt>q (kPa)</dt>
                <dd>{n(r.q_kpa, 2)}</dd></div>
              <div className="indice"><dt>σ₁&apos;/σ₃&apos;</dt>
                <dd>{n(r.razao_efetiva, 3)}</dd></div>
            </dl>
          </>
        ) : <Vazio>Sem ruptura apurada.</Vazio>}

        {/* O critério se escolhe na Envoltória (onda 275): ele depende
            do resultado de todos os corpos de prova. Aqui fica só o
            apontar à mão, que é escolher uma leitura nesta curva. */}
        <p className="explicacao">
          O critério se compara e se adota na aba <b>Envoltória</b>, com
          todos os corpos de prova lado a lado. Aqui dá para apontar a
          leitura à mão — clicando na curva ou na tabela de leituras.
        </p>
      </div>

      {/* As curvas antes da escolha (263): quem interpreta vê onde cada
          critério cai na curva, e pode apontar clicando nela. */}
      <GraficosDoCisalhamento cp={cp} tipo={tipo} apontando={salvando === 'apontar'}
        aoApontar={(eps) => gravar('apontar', () => editarTriaxialCp(cp.cp.id, {
          criterioRuptura: 'manual', rupturaDeformacaoPct: eps,
        }))} />

      {!cp.cisalhamento.length ? (
        <Vazio>
          Nenhuma leitura lançada. Importe o arquivo da prensa na aba
          «Amostra».
        </Vazio>
      ) : (
        <div className="rolar-lado"><table className="dados">
          <thead><tr>
            <th className="num">ε (%)</th>
            <th className="num">σd (kPa)</th>
            <th className="num">u (kPa)</th>
            <th className="num">σ₃ (kPa)</th>
            <th className="num">σ₃&apos; (kPa)</th>
            <th className="num">σ₁&apos; (kPa)</th>
            <th className="num">p&apos; (kPa)</th>
            <th className="num">q (kPa)</th>
            <th className="num">σ₁&apos;/σ₃&apos;</th>
            <th>Análise manual</th>
          </tr></thead>
          <tbody>
            {cp.cisalhamento.map((c: any) => {
              const ehRuptura = defRuptura !== null
                && Math.abs(Number(c.deformacao_axial_pct) - defRuptura) < 1e-9;
              return (
                <tr key={c.id} className={ehRuptura ? 'ruptura' : ''}>
                  <td className="num">{n(c.deformacao_axial_pct, 3)}</td>
                  <td className="num">{n(c.tensao_desvio_kpa, 2)}</td>
                  <td className="num">{n(c.poropressao_kpa, 2)}</td>
                  <td className="num">{n(c.tensao_confinante_kpa, 2)}</td>
                  <td className="num">{n(c.sigma3_efetiva_kpa, 2)}</td>
                  <td className="num">{n(c.sigma1_efetiva_kpa, 2)}</td>
                  <td className="num">{n(c.p_efetiva_kpa, 2)}</td>
                  <td className="num">{n(c.q_kpa, 2)}</td>
                  <td className="num">{n(c.razao_efetiva, 3)}</td>
                  <td>
                    {/* Apontar à mão (0235): esta leitura vira a ruptura
                        deste CP, sob o critério «manual». */}
                    <button type="button" className="discreto"
                      disabled={salvando === 'apontar' || c.descartada}
                      title="Adotar esta leitura como ruptura deste corpo de prova"
                      onClick={() => gravar('apontar', () => editarTriaxialCp(
                        cp.cp.id, {
                          criterioRuptura: 'manual',
                          rupturaDeformacaoPct: Number(c.deformacao_axial_pct),
                        }))}>
                      {ehRuptura ? 'Ruptura' : 'Apontar'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}
    </section>
  );
}

/**
 * Uma envoltória por critério de ruptura (0235): o critério aplicado a
 * todos os corpos de prova, e c, φ e R² de cada um, lado a lado. Quem
 * interpreta marca quais vão para o relatório final — o sistema calcula
 * todas e não escolhe nenhuma.
 */
function EnvoltoriasPorCriterio({ d, gravar, salvando }: {
  d: EnsaioTriaxial;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
}) {
  const lista = d.envoltorias ?? [];
  const inicial = useMemo(
    () => lista.filter((e: any) => e.no_laudo).map((e: any) => e.criterio),
    [lista]);
  const [marcados, setMarcados] = useState<string[]>(inicial);
  useEffect(() => setMarcados(inicial), [inicial]);
  if (!lista.length) return null;
  const efetiva = d.cabecalho?.tipo !== 'UU';
  const ciu = d.cabecalho?.tipo === 'CIU';
  const alternar = (c: string) => setMarcados((m) =>
    m.includes(c) ? m.filter((x) => x !== c) : [...m, c]);
  const mudou = marcados.slice().sort().join() !== inicial.slice().sort().join();
  return (
    <section className="bloco">
      <h2>Envoltórias por critério de ruptura</h2>
      <p className="explicacao">
        Cada linha aplica um critério a todos os corpos de prova e ajusta a
        mesma reta. Compare c e φ e marque quais envoltórias vão para o
        relatório final — a escolha é de quem interpreta. A envoltória
        adotada (o critério de cada corpo de prova) continua no laudo.
      </p>
      <div className="rolar-lado"><table className="dados">
        <thead><tr>
          <th>No laudo</th>
          <th>Critério</th>
          <th className="num">CPs</th>
          <th className="num">{efetiva ? "c' (kPa)" : 'c (kPa)'}</th>
          <th className="num">{efetiva ? "φ' (°)" : 'φ (°)'}</th>
          <th className="num">R²</th>
          {ciu && <th className="num">c (kPa)</th>}
          {ciu && <th className="num">φ (°)</th>}
          <th className="num">{efetiva ? "c' Camb. (kPa)" : 'c Camb. (kPa)'}</th>
          <th className="num">{efetiva ? "φ' Camb. (°)" : 'φ Camb. (°)'}</th>
        </tr></thead>
        <tbody>
          {lista.map((e: any) => (
            <tr key={e.criterio}>
              <td>
                <input type="checkbox" aria-label={`${e.criterio_nome} no laudo`}
                  checked={marcados.includes(e.criterio)}
                  disabled={!!e.motivo_sem_envoltoria && !marcados.includes(e.criterio)}
                  onChange={() => alternar(e.criterio)} />
              </td>
              <td>{e.criterio_nome}<br /><small>{e.referencia}</small></td>
              <td className="num">{e.pontos}</td>
              {e.motivo_sem_envoltoria ? (
                <td colSpan={ciu ? 7 : 5} className="nada">{e.motivo_sem_envoltoria}</td>
              ) : (
                <>
                  <td className="num">{n(e.coesao_kpa, 2)}</td>
                  <td className="num">{n(e.phi_graus, 2)}</td>
                  <td className="num">{n(e.r2, 4)}</td>
                  {ciu && <td className="num">{n(e.coesao_total_kpa, 2)}</td>}
                  {ciu && <td className="num">{n(e.phi_total_graus, 2)}</td>}
                  <td className="num">{n(e.coesao_camb_kpa, 2)}</td>
                  <td className="num">{n(e.phi_camb_graus, 2)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table></div>
      <button type="button" className="primario"
        disabled={!mudou || salvando === 'no-laudo'}
        onClick={() => gravar('no-laudo',
          () => gravarCriteriosNoLaudo(d.cabecalho.ensaio_id, marcados))}>
        {salvando === 'no-laudo' ? 'Gravando…' : 'Gravar a escolha do relatório'}
      </button>
    </section>
  );
}

/**
 * Os critérios de ruptura de TODOS os corpos de prova, num lugar só
 * (onda 275).
 *
 * «Os critérios de ruptura dos CPs devem ser avaliados na aba
 * envoltória. Fica concentrado tudo lá, mais fácil analisar, pois
 * depende do resultado de todos os cps» — Humberto, 04/10/2026.
 *
 * Uma linha por critério, uma coluna por corpo de prova: o ponto que
 * aquele critério escolhe naquela curva (ε e q), o adotado em destaque,
 * e ao lado o c' e o φ' que a envoltória daquele critério daria. Quem
 * interpreta compara a linha inteira — é a envoltória que importa, e
 * ela depende de todos os CPs. O padrão do ensaio (para os CPs sem
 * critério próprio), a deformação limite e o diagrama do laudo ficam no
 * mesmo bloco. O apontar à mão continua na curva da aba «Cisalhamento»,
 * porque apontar é escolher uma leitura numa curva.
 */
function CriteriosDosCorpos({ d, gravar, salvando, aoApontar }: {
  d: EnsaioTriaxial;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  aoApontar(cpId: string): void;
}) {
  const cab = d.cabecalho;
  const [padrao, setPadrao] = useState(() => ({
    criterio: (cab?.criterio_ruptura ?? null) as string | null,
    fonte: cab?.criterio_ruptura_fonte ?? '',
    limite: paraCampo(cab?.deformacao_limite_pct),
    espaco: (cab?.espaco_tensoes ?? 'mit') as string,
  }));
  useEffect(() => {
    setPadrao({
      criterio: (cab?.criterio_ruptura ?? null) as string | null,
      fonte: cab?.criterio_ruptura_fonte ?? '',
      limite: paraCampo(cab?.deformacao_limite_pct),
      espaco: (cab?.espaco_tensoes ?? 'mit') as string,
    });
  }, [cab?.criterio_ruptura, cab?.criterio_ruptura_fonte,
    cab?.deformacao_limite_pct, cab?.espaco_tensoes]);
  if (!cab) return null;

  const corpos = (d.corpos ?? []).filter((c: any) => c.situacao !== 'descartado');
  const linhas = d.criterios ?? [];
  const envolt = new Map((d.envoltorias ?? []).map((e: any) => [e.criterio, e]));
  const efetiva = cab.tipo !== 'UU';
  const celula = (cpId: string, criterio: string) =>
    linhas.find((l: any) => l.cp_id === cpId && l.criterio === criterio);
  const ocupado = salvando === 'criterio' || salvando === 'criterio-todos';
  const mudouPadrao = (padrao.criterio ?? '') !== (cab.criterio_ruptura ?? '')
    || padrao.fonte !== (cab.criterio_ruptura_fonte ?? '')
    || paraNumero(padrao.limite) !== paraNumero(paraCampo(cab.deformacao_limite_pct))
    || padrao.espaco !== (cab.espaco_tensoes ?? 'mit');

  const adotarEmTodos = (criterio: string) => gravar('criterio-todos', async () => {
    for (const c of corpos) {
      await editarTriaxialCp(c.id, { criterioRuptura: criterio });
    }
  });

  return (
    <section className="bloco">
      <h2>Critérios de ruptura dos corpos de prova</h2>
      <p className="explicacao">
        Cada linha é um critério; cada coluna, um corpo de prova. Na célula,
        o ponto que aquele critério escolhe naquela curva — calculado pelo
        sistema sobre as leituras que existem (nenhum interpola). À direita,
        o {efetiva ? "c' e o φ'" : 'c e o φ'} que a envoltória daria se todos
        os corpos de prova rompessem por aquele critério. Adote por corpo de
        prova ou na linha inteira; o apontar à mão se faz na curva, na aba
        «Cisalhamento».
      </p>

      {!corpos.length ? (
        <Vazio>Nenhum corpo de prova ainda.</Vazio>
      ) : (
        <div className="rolar-lado"><table className="dados criterios-por-cp">
          <thead><tr>
            <th>Critério</th>
            {corpos.map((c: any) => (
              <th key={c.id} className="num">{c.identificacao}</th>
            ))}
            <th className="num">{efetiva ? "c' (kPa)" : 'c (kPa)'}</th>
            <th className="num">{efetiva ? "φ' (°)" : 'φ (°)'}</th>
            <th className="num">R²</th>
            <th />
          </tr></thead>
          <tbody>
            {CRITERIOS.map((k) => {
              const manual = APONTADOS.has(k.valor);
              const cel = corpos.map((c: any) => celula(c.id, k.valor));
              const todosComPonto = cel.every((l: any) => l?.deformacao_axial_pct != null);
              const todosAdotados = cel.every((l: any) => l?.adotado);
              const e: any = envolt.get(k.valor);
              const nome = cel.find(Boolean)?.criterio_nome ?? k.texto;
              const ref = cel.find(Boolean)?.referencia ?? k.detalhe;
              return (
                <tr key={k.valor} className={todosAdotados ? 'ruptura' : ''}>
                  <td>{nome}<br /><small>{ref}</small></td>
                  {corpos.map((c: any, i: number) => {
                    const l: any = cel[i];
                    const temPonto = l?.deformacao_axial_pct != null;
                    return (
                      <td key={c.id} className={`num${l?.adotado ? ' adotado' : ''}`}>
                        {temPonto ? (
                          <>
                            ε {n(l.deformacao_axial_pct, 3)} %<br />
                            q {n(l.q_kpa, 2)}
                          </>
                        ) : (
                          <small className="nada" title={l?.motivo ?? ''}>
                            {manual ? 'não apontado' : (l?.motivo ?? '—')}
                          </small>
                        )}
                        <br />
                        {l?.adotado ? (
                          <b>Adotado{c.criterio_ruptura ? '' : ' (padrão)'}</b>
                        ) : manual ? (
                          <button type="button" className="discreto"
                            onClick={() => aoApontar(c.id)}
                            title="Abre a curva deste corpo de prova na aba «Cisalhamento» para apontar a leitura">
                            Apontar na curva
                          </button>
                        ) : (
                          <button type="button" className="discreto"
                            disabled={ocupado || !temPonto}
                            aria-label={`Adotar ${nome} em ${c.identificacao}`}
                            onClick={() => gravar('criterio', () => editarTriaxialCp(
                              c.id, { criterioRuptura: k.valor }))}>
                            Adotar
                          </button>
                        )}
                      </td>
                    );
                  })}
                  {!e || e.motivo_sem_envoltoria ? (
                    <td colSpan={3} className="nada">
                      <small>{e?.motivo_sem_envoltoria ?? '—'}</small>
                    </td>
                  ) : (
                    <>
                      <td className="num">{n(e.coesao_kpa, 2)}</td>
                      <td className="num">{n(e.phi_graus, 2)}</td>
                      <td className="num">{n(e.r2, 4)}</td>
                    </>
                  )}
                  <td>
                    {!manual && !todosAdotados && (
                      <button type="button" className="discreto"
                        disabled={ocupado || !todosComPonto}
                        title={todosComPonto ? undefined
                          : 'Algum corpo de prova não tem ponto por este critério.'}
                        onClick={() => adotarEmTodos(k.valor)}>
                        {salvando === 'criterio-todos' ? 'Gravando…' : 'Adotar em todos'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}

      <h3>Padrão do ensaio e diagrama do laudo</h3>
      <p className="explicacao">
        O padrão vale para os corpos de prova sem critério próprio (marcados
        «padrão» na grade). A referência vai impressa no laudo: quem lê
        precisa saber sob qual regra o ângulo saiu.
      </p>
      <div className="grade-campos">
        <Escolha rotulo="Critério padrão do ensaio" opcoes={CRITERIOS}
          valor={padrao.criterio}
          aoEscolher={(v) => setPadrao((x) => ({ ...x, criterio: v }))} />
        <Campo rotulo="Referência do critério" value={padrao.fonte}
          placeholder="De Campos e Carrillo (1995)"
          onChange={(ev) => setPadrao((x) => ({ ...x, fonte: ev.target.value }))} />
        <CampoNumerico rotulo="Deformação axial limite (%)" value={padrao.limite}
          dica="Usada pelo critério «Deformação limitada». Sem ela, esse critério não produz ponto."
          onChange={(ev) => setPadrao((x) => ({ ...x, limite: ev.target.value }))} />
        <Escolha rotulo="Diagrama p–q no laudo" opcoes={ESPACOS}
          valor={padrao.espaco} limpavel={false}
          aoEscolher={(v) => setPadrao((x) => ({ ...x, espaco: v ?? 'mit' }))} />
      </div>
      <button type="button" className="primario"
        disabled={!mudouPadrao || salvando === 'padrao'}
        onClick={() => gravar('padrao', () => gravarTriaxialFicha(cab.ensaio_id,
          corpoDaFicha(cab, {
            criterioRuptura: padrao.criterio ?? undefined,
            criterioRupturaFonte: padrao.fonte || undefined,
            deformacaoLimitePct: paraNumero(padrao.limite),
            espacoTensoes: padrao.espaco || undefined,
          })))}>
        {salvando === 'padrao' ? 'Gravando…' : 'Gravar o padrão'}
      </button>
    </section>
  );
}

/**
 * O corpo do `POST /triaxial/:id/ficha` a partir do cabeçalho gravado,
 * com as mudanças por cima. A rota grava a ficha inteira; quem muda só o
 * critério (a Envoltória) reenvia o resto como está — sem isto, gravar o
 * critério apagaria a geometria nominal e o ρs.
 */
function corpoDaFicha(cab: any, mudancas: Record<string, unknown>) {
  const num = (v: unknown) => (v == null || v === '' ? undefined : Number(v));
  return {
    tipo: cab.tipo,
    diametroNominalMm: num(cab.diametro_nominal_mm),
    alturaNominalMm: num(cab.altura_nominal_mm),
    massaEspecificaGraos: num(cab.massa_especifica_graos),
    graosAssumida: !!cab.graos_assumida,
    espessuraMembranaMm: num(cab.espessura_membrana_mm),
    moduloMembranaKpa: num(cab.modulo_membrana_kpa),
    metodoSaturacao: cab.metodo_saturacao ?? undefined,
    criterioRuptura: cab.criterio_ruptura ?? undefined,
    criterioRupturaFonte: cab.criterio_ruptura_fonte || undefined,
    deformacaoLimitePct: num(cab.deformacao_limite_pct),
    espacoTensoes: cab.espaco_tensoes ?? undefined,
    observacoes: cab.observacoes || undefined,
    ...mudancas,
  };
}

// ══ Fase 5 · envoltória ═══════════════════════════════════════════════

function Envoltoria({ d, gravar, salvando, aoApontar }: {
  d: EnsaioTriaxial;
  gravar(marca: string, f: () => Promise<unknown>): Promise<boolean>;
  salvando: string | null;
  /** Leva à aba «Cisalhamento» com este CP aberto, para apontar na curva. */
  aoApontar(cpId: string): void;
}) {
  const e = d.envoltoria;
  const efetiva = e?.base !== 'total';
  return (
    <>
      <CriteriosDosCorpos d={d} gravar={gravar} salvando={salvando}
        aoApontar={aoApontar} />

      <section className="bloco">
        <h2>Envoltória de resistência</h2>
        <p className="explicacao">
          A reta q = a + p{efetiva ? "'" : ''}·tg α é ajustada por mínimos
          quadrados aos pontos de ruptura, e convertida pelas identidades
          sen φ = tg α e c = a / cos φ (diagrama MIT). No diagrama de
          Cambridge, q = a + M·p{efetiva ? "'" : ''}, com sen φ = 3M/(6+M)
          e c = a·(3 − sen φ)/(6·cos φ). A conversão é geometria, não
          escolha — o que é escolha, e por isso é declarado, é o critério
          que define cada ponto.
        </p>

        {!e ? (
          <Vazio>
            Nenhum corpo de prova chegou a um ponto de ruptura. A
            envoltória precisa de pelo menos dois.
          </Vazio>
        ) : e.motivo_sem_envoltoria ? (
          <Aviso tipo="atencao" titulo="Envoltória não ajustada">
            {e.motivo_sem_envoltoria}.
          </Aviso>
        ) : (
          <dl className="grade-indices destaque">
            <div className="indice"><dt>Base</dt>
              <dd>{efetiva ? 'Efetivas' : 'Totais'}</dd></div>
            <div className="indice"><dt>Pontos</dt>
              <dd>{e.pontos}</dd></div>
            <div className="indice"><dt>a (kPa)</dt>
              <dd>{n(e.intercepto_kpa, 3)}</dd></div>
            <div className="indice"><dt>tg α</dt>
              <dd>{n(e.tg_alfa, 5)}</dd></div>
            <div className="indice"><dt>R²</dt>
              <dd>{n(e.r2, 4)}</dd></div>
            <div className="indice grande">
              <dt>{efetiva ? "φ' (°)" : 'φ (°)'}</dt>
              <dd>{n(e.phi_graus, 2)}</dd></div>
            <div className="indice grande">
              <dt>{efetiva ? "c' (kPa)" : 'c (kPa)'}</dt>
              <dd>{n(e.coesao_kpa, 2)}</dd></div>
            {/* Cambridge (0235): q = a + M·p, sen φ = 3M/(6+M). */}
            <div className="indice"><dt>M (Cambridge)</dt>
              <dd>{n(e.m_camb, 5)}</dd></div>
            <div className="indice"><dt>R² (Cambridge)</dt>
              <dd>{n(e.r2_camb, 4)}</dd></div>
            <div className="indice grande">
              <dt>{efetiva ? "φ' Cambridge (°)" : 'φ Cambridge (°)'}</dt>
              <dd>{n(e.phi_camb_graus, 2)}</dd></div>
            <div className="indice grande">
              <dt>{efetiva ? "c' Cambridge (kPa)" : 'c Cambridge (kPa)'}</dt>
              <dd>{n(e.coesao_camb_kpa, 2)}</dd></div>
          </dl>
        )}
      </section>

      {e && !e.motivo_sem_envoltoria && (
        <section className="bloco">
          <h2>Gráficos da envoltória</h2>
          <p className="explicacao">
            As trajetórias de tensões de cada corpo de prova até a ruptura,
            a envoltória adotada e os círculos de Mohr — os mesmos do
            laudo, aqui para conferir antes de gerá-lo. «Comparar com»
            desenha, tracejada, a reta de outro critério.
          </p>
          <GraficosDaEnvoltoria d={d}
            espacoInicial={d.cabecalho?.espaco_tensoes ?? 'mit'} />
        </section>
      )}

      <EnvoltoriasPorCriterio d={d} gravar={gravar} salvando={salvando} />

      <section className="bloco">
        <h2>Os pontos que sustentam a envoltória</h2>
        {!d.rupturas.length ? (
          <Vazio>Nenhum corpo de prova.</Vazio>
        ) : (
          <div className="rolar-lado"><table className="dados">
            <thead><tr>
              <th>CP</th>
              <th>Critério</th>
              <th className="num">ε (%)</th>
              <th className="num">σd (kPa)</th>
              <th className="num">σ₃&apos; (kPa)</th>
              <th className="num">σ₁&apos; (kPa)</th>
              <th className="num">p&apos; (kPa)</th>
              <th className="num">q (kPa)</th>
            </tr></thead>
            <tbody>
              {d.rupturas.map((r: any) => (
                <tr key={r.cp_id} className={r.motivo_sem_ruptura ? 'sem' : ''}>
                  <td>{r.identificacao}</td>
                  {r.motivo_sem_ruptura ? (
                    <td colSpan={7} className="nada">
                      sem ponto de ruptura — {r.motivo_sem_ruptura}
                    </td>
                  ) : (
                    <>
                      <td>{CRITERIO_TEXTO[r.criterio] ?? r.criterio}</td>
                      <td className="num">{n(r.deformacao_axial_pct, 3)}</td>
                      <td className="num">{n(r.tensao_desvio_kpa, 2)}</td>
                      <td className="num">{n(r.sigma3_efetiva_kpa, 2)}</td>
                      <td className="num">{n(r.sigma1_efetiva_kpa, 2)}</td>
                      <td className="num">{n(r.p_efetiva_kpa, 2)}</td>
                      <td className="num">{n(r.q_kpa, 2)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        <p className="explicacao">
          Os círculos de Mohr e as trajetórias p&apos;–q&apos; estão
          desenhados acima, em «Gráficos da envoltória», e saem iguais no
          laudo.
        </p>
      </section>
    </>
  );
}
